import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { createContext, createElement, useContext } from "react";
import { Dialog, Knopf } from "../me/index.js";
import { RueckfrageGastgeber } from "./VerwerfenRueckfrage.js";
import { StrikteEbene, useImModal, useStrikteWege } from "./strikteHuelle.js";
import "./Modal.css";
/** Steht der Fuss in einer FRAGE (`Modal frage`)? Dann ist „Abbrechen“ leise wie in `bestaetigen()` (Entscheid 117); die
 *  Frage sagt es dem Fuss, nicht der Aufrufer — so hat „Abbrechen“ in jeder Frage dieselbe Art. */
const InFrage = createContext(false);
/** Die vier Stufen in Pixeln des Entwurfs — `me-dialog` rechnet sie selbst in rem um. */
const BREITE_PX = {
    standard: 480,
    mittel: 640,
    breit: 900,
    weit: 1200,
};
export function Modal(p) {
    const imModal = useImModal();
    const breitePx = BREITE_PX[p.breite ?? "standard"];
    /*
     * Die Wege hinaus (Riegel am Rahmen, natives Schliessen abgefangen, Öffnen im selben
     * Commit) stehen seit dem Paket „Editor-Arbeitsdialog“ (09.10.2026) gemeinsam mit dem
     * `Arbeitsdialog` in strikteHuelle.tsx — dort auch die Arbeitsteilung am Riegel. Die
     * Fokus-Rückgabe an den Auslöser beim Aushängen macht me-dialog selbst (aff0190).
     */
    const { anker, steuerung } = useStrikteWege(p);
    /*
     * `strikt`: Ein Klick auf den Schleier schliesst NICHT (das Element prüft das selbst),
     * das Kreuz im Kopf bleibt sichtbar und läuft über `onAbbrechen` (Entscheid 36).
     * `offen` bleibt fest auf true — das Modal ist offen, solange es eingehängt ist.
     * Die Klasse `ki-modal` am Host ist nur Kennzeichen (die Einklapp-Regel der Verwaltung
     * nimmt ihn aus, Verwaltung.css); die Optik kommt vom Element.
     * `unter="seitlich"` (Entscheid Dirk 14): Passt der Subdialog nicht neben seine Eltern
     * (weite Maske 1200 + 480 auf 1280 px, Befund 4), stapelt `me-dialog` ihn selbst.
     * Den zugänglichen Namen gibt das Element seiner Ebene aus dem Titel — als
     * `aria-labelledby` und zusätzlich als `aria-label` (für Prüfwerkzeuge, die dem Verweis in
     * den Slot nicht folgen); ein eigenes `aria-label` am Host braucht es nicht mehr.
     * Im Inhalt einer anderen Hülle wird das Modal per Portal an <body> gezeichnet
     * (`StrikteEbene`, Review K1/K2).
     */
    return (_jsx(StrikteEbene, { imModal: imModal, children: _jsxs(Dialog, { ref: steuerung, offen: true, strikt: true, lage: p.frage === true ? "mitte" : "rechts", breite: breitePx, unter: p.gestapelt === true || p.frage === true ? "stapel" : "seitlich", ...(p.gefahr === true ? { gefahr: true } : {}), ...(p.frage === true ? { art: "frage" } : {}), ...(p.verlauf === true ? { verlauf: true } : {}), className: "ki-modal", titel: _jsx("span", { ref: anker, children: p.titel }), children: [_jsx(InFrage.Provider, { value: p.frage === true, children: p.children }), _jsx(RueckfrageGastgeber, {})] }) }));
}
/**
 * Die Fußleiste eines strikten Modals — Speichern links, Abbrechen rechts, in jeder Maske
 * dieselbe Reihenfolge und dieselben Wörter.
 *
 * Sie steht hier und nicht in jeder Maske abgeschrieben, weil sie die Hausregel TRÄGT:
 * Solange jede Maske ihre zwei Knöpfe selbst zeichnete, konnte eine davon ein drittes
 * „Schliessen" dazustellen.
 *
 * Seit ME-Etappe 4 (P8) sind beide Knöpfe `me-knopf` — der bestätigende `primaer` (bei
 * `gefahr` `art="gefahr"`), „Abbrechen“ `sekundaer`, in einer Frage (`Modal frage`) `leise` wie in `bestaetigen()`
 * (Entscheid 117, „Öffnen statt Springen“ Aufgabe 5); bis dahin standen sie als native
 * `<button>` mit eigenem CSS (Modal.css), nur die Gefahr-Frage (P4b) war schon umgestellt.
 * Die Klassen `ki-modal__primaer`/`ki-modal__knopf` bleiben als Kennzeichen für Tests und
 * E2E, sie zeichnen nichts. Ein gesperrter Knopf ist `deaktiviert`; ein Klick auf seinen
 * Host wird zusätzlich verworfen (der Host selbst bleibt klickbar).
 */
export function ModalFuss(p) {
    const speichernAus = p.speichernGesperrt === true;
    const abbrechenAus = p.abbrechenGesperrt === true;
    const gefahr = p.gefahr === true;
    const frage = useContext(InFrage);
    /*
     * `slot="fuss"`: Der Fuss liegt als Kind von `Modal` im Inhalt, und `me-dialog` verteilt
     * ihn in seine Fußzeile (klebend, nur der Inhalt rollt). Dafür muss `ModalFuss`
     * UNMITTELBARES Kind von `Modal` sein — so steht er in allen Masken. `display: contents`
     * macht die zwei Knöpfe selbst zu den Kindern der Knopfzeile des Elements.
     */
    return (_jsxs("div", { slot: "fuss", className: "ki-modal__aktionen", style: { display: "contents" }, children: [_jsx(Knopf, { art: gefahr ? "gefahr" : "primaer", className: "ki-modal__primaer", deaktiviert: speichernAus, ...(p.speichernLabel === undefined ? {} : { "aria-label": p.speichernLabel }), onClick: () => { if (!speichernAus)
                    p.onSpeichern(); }, children: p.speichernText }), p.nurSchliessen !== true && (_jsx(Knopf, { art: frage ? "leise" : "sekundaer", className: "ki-modal__knopf", deaktiviert: abbrechenAus, autoFocus: gefahr, onClick: () => { if (!abbrechenAus)
                    p.onAbbrechen(); }, children: "Abbrechen" }))] }));
}
/** Ein `me-knopf` UNMITTELBAR im Slot des Dialogs (W-15: die Hülle `Knopf` reicht `slot` nicht durch); `data-rolle` wie
 *  `bestaetigen()` (bestaetigen, alternative, abbrechen) — dieselben Merkmale für Tests und Helfer in beiden Wegen. */
function fussKnopf(rolle, slot, art, text, onClick, gesperrt, fokus = false) {
    return createElement("me-knopf", { slot, art, "data-rolle": rolle, ...(gesperrt ? { deaktiviert: "" } : {}),
        ...(fokus ? { anfangsfokus: "" } : {}), onClick: () => { if (!gesperrt)
            onClick(); } }, text);
}
/**
 * Der Fuss einer FRAGE (`Modal frage`) mit einer Alternative zur Aktion, nach Entscheid 117 (me-design 77644ed) wie
 * `bestaetigen({ alternative })`: Hauptaktion `primaer` mit dem Anfangsfokus, „Abbrechen“ `leise` (beide Slot `fuss`),
 * Alternative `sekundaer` im Slot `fuss-links`. Schreibtisch [Alternative] … [Abbrechen] [Hauptaktion]; am Telefon (Blatt)
 * ordnet me-dialog über `::slotted([art])` Hauptaktion, Alternative, Abbrechen untereinander.
 *
 * Darum stehen die Knöpfe ohne Träger im Dialog (`::slotted` sähe nur den Träger) und tragen den Slot VON ANFANG AN: Ein
 * nachträglich gesetzter Slot (Ref) liesse die Fusszeile beim Öffnen leer und verborgen — der Anfangsfokus ginge ans Kreuz
 * (gemessen 03.11.2026 in Chromium). Unmittelbares Kind von `Modal`, wie `ModalFuss`.
 */
export function FrageFuss(p) {
    const aus = p.gesperrt === true;
    return (_jsxs(_Fragment, { children: [p.alternative !== undefined && fussKnopf("alternative", "fuss-links", "sekundaer", p.alternative.text, p.alternative.onClick, aus), fussKnopf("abbrechen", "fuss", "leise", "Abbrechen", p.onAbbrechen, aus), fussKnopf("bestaetigen", "fuss", "primaer", p.aktionText, p.onAktion, aus, true)] }));
}
// Die Rückfrage vor dem Abbrechen steht seit ME-Etappe 3 als Subdialog in
// VerwerfenRueckfrage.tsx; der Name bleibt hier erreichbar.
export { darfAbbrechen } from "./VerwerfenRueckfrage.js";
//# sourceMappingURL=Modal.js.map